/*
 * Copyright (c) 2023. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import type { UINotification } from '@poweralpha/common';
import { useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  notificationContainerId,
  showNotificationOverflowToast,
  showNotificationToast,
} from './showNotificationToast';

// The toast stack shows at most this many new notifications; the rest go into a "+N more" pill.
export const MAX_NOTIFICATION_TOASTS = 3;

function useNotificationToasts(
  notifications: Array<UINotification> | undefined,
  handleClick: (notif: UINotification, redirect?: boolean) => void,
  onOpenCenter?: () => void,
) {
  const unread = useMemo(() => notifications?.filter((notif) => !notif.viewed_at), [notifications]);
  const seenIdsRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!unread) return;

    const unreadIds = new Set(unread.map(({ id }) => id));
    const previousUnreadIds = seenIdsRef.current ?? new Set<string>();

    if (unread.length === 0) {
      if (previousUnreadIds.size > 0) {
        toast.dismiss({ containerId: notificationContainerId });
        toast.clearWaitingQueue({ containerId: notificationContainerId });
      }
      seenIdsRef.current = unreadIds;
      return;
    }

    // Unread arrives newest first: toast the newest few (oldest first, so the newest ends up
    // in front of the stack); the "+N more" pill goes first so it sits at the back.
    const nextNotifs = unread.filter(({ id }) => !previousUnreadIds.has(id));
    const shown = onOpenCenter ? nextNotifs.slice(0, MAX_NOTIFICATION_TOASTS) : nextNotifs;
    if (onOpenCenter && nextNotifs.length > shown.length) {
      showNotificationOverflowToast(nextNotifs.length - shown.length, onOpenCenter);
    }
    [...shown].reverse().forEach((notif) => {
      showNotificationToast(notif, handleClick);
    });
    seenIdsRef.current = unreadIds;
  }, [handleClick, onOpenCenter, unread]);
}

export default useNotificationToasts;
